import { HttpErrorResponse } from '@angular/common/http';
import { AfterViewInit, Component, ViewChild } from '@angular/core';
import { AdministratorsService, IOcbCUGlobalAdministrator, IOcbGlobalAdministrator } from '@app/core';
import { TranslateService } from '@ngx-translate/core';
import { DxDataGridComponent, DxValidationGroupComponent } from 'devextreme-angular';
import { confirm } from 'devextreme/ui/dialog';
import notify from 'devextreme/ui/notify';
import { forkJoin, from as fromPromise, Observable } from 'rxjs';
import { filter, finalize, mergeMap, tap } from 'rxjs/operators';

@Component({
    selector: 'app-global-admins',
    templateUrl: './global-admins.component.html',
    styleUrls: ['./global-admins.component.scss'],
})
export class GlobalAdminsComponent implements AfterViewInit {

    @ViewChild('adminsGrid')
    public adminsGrid: DxDataGridComponent;

    @ViewChild('adminValidationGroup')
    public adminValidationGroup: DxValidationGroupComponent;

    public admins: IOcbGlobalAdministrator[] = [];

    public createPopupVisible: boolean = false;
    public editPopupVisible: boolean = false;

    public editAdmin: IOcbGlobalAdministrator;
    public newAdmin: IOcbGlobalAdministrator;

    constructor(
        private adminService: AdministratorsService,
        private translateService: TranslateService) {
    }

    public ngAfterViewInit() {
        this.loadDataSource();
    }

    /**
     * Display popup
     * @param e Handle event
     */
    public onRowDblClick(e) {
        if (e && e.data) {
            this.showEditPopup(e, e.data);
        }
    }

    /**
     * Set context menu items
     * @param e Handle event
     */
    public onContextMenuPreparing(e) {
        if (e.row && e.row.rowType === 'data') {
            forkJoin(
                // Get translation messages
                this.translateService.get('_commons.edit'),
                this.translateService.get('_commons.copy'),
                this.translateService.get('_commons.delete'),
            ).subscribe(
                messages => {
                    e.items = [
                        { text: messages[0], icon: 'edit', onItemClick: (ev) => this.showEditPopup(ev, e.row.data) },
                        { text: messages[1], icon: 'copy', onItemClick: (ev) => this.showCreatePopup(ev, e.row.data) },
                        { text: messages[2], icon: 'trash', onItemClick: (ev) => this.deleteAdmin(ev, e.row.data) },
                    ];
                },
                err => this.handleError(err),
            );
        }
    }

    /**
     * Set the custom properties of fields in edit mode
     */
    public onEditorPreparing(e) {
        switch (e.dataField) {
            case 'canManageEnvironments':
            case 'canManageGlobalAdministrators':
                e.editorName = 'dxSwitch';
                break;
        }
    }

    /**
     * Shows the admininistrator creation popup.
     * @param e Handle event.
     * @param copyAdmin Copy properties from this admin.
     */
    public showCreatePopup(e?, copyAdmin?: IOcbGlobalAdministrator): void {
        if (e) {
            e.event.stopPropagation();
        }
        let permissions: IOcbCUGlobalAdministrator = {};

        if (copyAdmin) {
            permissions = {
                canManageEnvironments: copyAdmin.canManageEnvironments,
                canManageGlobalAdministrators: copyAdmin.canManageGlobalAdministrators,
            };
        }
        else {
            permissions = {
                canManageEnvironments: false,
                canManageGlobalAdministrators: false,
            };
        }

        this.newAdmin = {
            user: null,
            ...permissions,
        };

        this.createPopupVisible = true;
    }

    /**
     * Edit an administrator properties
     * @param e Handle event.
     * @param admin Edited administrator.
     */
    public showEditPopup(e, admin: IOcbGlobalAdministrator): void {
        if (e && e.event) {
            e.event.stopPropagation();
        }
        
        this.editAdmin = admin;
        this.editPopupVisible = true;
    }

    /**
     * Creates the new administrator
     * @param admin New admin.
     */
    public createAdmin(admin: IOcbGlobalAdministrator): void {
        if (this.adminValidationGroup.instance.validate().isValid) {
            this.createPopupVisible = false;
            this.showLoading$('_commons.loading')
                .pipe(
                    mergeMap(() => this.adminService.addGlobalAdministrator(admin.user.id, admin)),
                    mergeMap(_ => this.translateService.get('globalAdmins.msgAddAdminComplete')),
                    finalize(() => this.adminsGrid.instance.endCustomLoading()),
                )
                .subscribe(
                    message => {
                        this.loadDataSource();
                        // Notify
                        notify(message, 'success', 3000);
                    },
                    err => this.handleError(err),
                );
        }
    }

    /**
     * Updates an administrator
     * @param admin Updated admin.
     */
    public updateAdmin(admin: IOcbGlobalAdministrator): void {
        this.editPopupVisible = false;
        this.showLoading$('_commons.loading')
            .pipe(
                mergeMap(() => this.adminService.updateGlobalAdministrator(admin.user.id, admin)),
                mergeMap(_ => this.translateService.get('globalAdmins.msgUpdateAdminComplete')),
                finalize(() => this.adminsGrid.instance.endCustomLoading()),
            )
            .subscribe(
                message => {
                    this.loadDataSource();
                    // Notify the success
                    notify(message, 'success', 3000);
                },
                err => this.handleError(err),
            );
    }

    /**
     * Deletes an administrator.
     * @param e Handle event.
     * @param admin Deleted admin.
     */
    public deleteAdmin(e, admin: IOcbGlobalAdministrator): void {
        e.event.stopPropagation();
        forkJoin(
            // Get translation messages
            this.showLoading$('_commons.loading'),
            this.translateService.get('globalAdmins.msgDeleteAdminConfirm', { name: admin.user.name }),
            this.translateService.get('globalAdmins.msgDeleteAdminConfirmTitle'),
        )
            .pipe(
                // Display confirm popup
                mergeMap(messages => fromPromise(confirm(messages[1], messages[2]))),
                filter(accepted => accepted),
                // Send the DELETE request
                mergeMap(_ => this.adminService.deleteGlobalAdministrator(admin.user.id)),
                // Get the confirmation message
                mergeMap(_ => this.translateService.get('globalAdmins.msgDeleteAdminComplete')),
                finalize(() => this.adminsGrid.instance.endCustomLoading()),
            ).subscribe(
                message => {
                    this.loadDataSource();
                    // Notify the success
                    notify(message, 'success', 3000);
                },
                err => this.handleError(err),
            );
    }

    private loadDataSource(): void {
        // Display loader and fetch global admins
        this.showLoading$('_commons.loading')
            .pipe(
                mergeMap(_ => this.adminService.getGlobalAdministrators()),
                finalize(() => this.adminsGrid.instance.endCustomLoading()),
            )
            .subscribe(
                res => this.admins = res,
                err => this.handleError(err),
            );
    }

    /**
     * Displays a loader over the data grid, with a localized message.
     * @param messageKey Localized message key.
     */
    private showLoading$(messageKey: string): Observable<void> {
        return this.translateService.get(messageKey).pipe(
            tap(message => this.adminsGrid.instance.beginCustomLoading(`${message}...`)),
        );
    }

    /**
     * Displays error message in notification
     */
    private handleError(error: any) {
        let message: '';
        if (error instanceof HttpErrorResponse) {
            if (error.error.hasOwnProperty('message')) {
                message = error.error.message;
            }
            else if (error.error.hasOwnProperty('name')) {
                message = error.error.name;
            }
        }
        else {
            message = error;
        }

        notify(message, 'error', 3000);
    }
}
